import React, { useState, useEffect, useCallback, useMemo } from 'react';
import {
  Box,
  Button,
  Card,
  CardContent,
  Typography,
  Table,
  TableHead,
  TableBody,
  TableRow,
  TableCell,
  CircularProgress,
  Alert,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Chip,
  Stack,
} from '@mui/material';
import {
  Add as AddIcon,
  Visibility as ViewIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  ContentCopy as CopyIcon,
} from '@mui/icons-material';
import { toast } from 'react-toastify';
import { alpha } from '@mui/material/styles';
import api from '../../utils/api';
import { useAuth } from '../../context/AuthContext';
import UserGroupAssignmentPicker from './UserGroupAssignmentPicker';
import {
  formatUserCreationTemplateTitle,
  normalizeTemplateShortName,
} from '../../utils/userCreationTemplate';

export default function UserCreationTemplates() {
  const { hasPermission } = useAuth();
  const canModify = hasPermission('users_modify');

  const [loading, setLoading] = useState(true);
  const [builtins, setBuiltins] = useState([]);
  const [customs, setCustoms] = useState([]);
  const [groups, setGroups] = useState([]);

  const [viewOpen, setViewOpen] = useState(false);
  const [viewRow, setViewRow] = useState(null);

  const [editOpen, setEditOpen] = useState(false);
  const [editMode, setEditMode] = useState('create'); // create | edit
  const [editId, setEditId] = useState(null);
  const [editName, setEditName] = useState('');
  const [editDescription, setEditDescription] = useState('');
  const [editGroupIds, setEditGroupIds] = useState([]);
  const [editSaving, setEditSaving] = useState(false);

  const loadAll = useCallback(async () => {
    setLoading(true);
    try {
      const [tr, gr] = await Promise.all([
        api.get('/users/group-assignment-templates'),
        api.get('/groups', { params: { limit: 500 } }),
      ]);
      setBuiltins(tr.data?.data?.builtins || []);
      setCustoms(tr.data?.data?.customs || []);
      setGroups(gr.data?.data || []);
    } catch (e) {
      toast.error(e.response?.data?.message || 'Failed to load templates');
      setBuiltins([]);
      setCustoms([]);
      setGroups([]);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadAll();
  }, [loadAll]);

  const rows = useMemo(() => {
    const bi = builtins.map((t) => ({ ...t, rowKind: 'builtin' }));
    const cu = customs.map((t) => ({ ...t, rowKind: 'custom' }));
    return [...bi, ...cu];
  }, [builtins, customs]);

  const openView = (row) => {
    setViewRow(row);
    setViewOpen(true);
  };

  const closeView = () => {
    setViewOpen(false);
    setViewRow(null);
  };

  const openCreate = () => {
    setEditMode('create');
    setEditId(null);
    setEditName('');
    setEditDescription('');
    setEditGroupIds([]);
    setEditOpen(true);
  };

  const openEditCustom = (row) => {
    setEditMode('edit');
    setEditId(row.id);
    setEditName(row.name || '');
    setEditDescription(row.description || '');
    setEditGroupIds(Array.isArray(row.group_ids) ? [...row.group_ids] : []);
    setEditOpen(true);
  };

  const copyBuiltinAsCustom = (row) => {
    setEditMode('create');
    setEditId(null);
    setEditName(`${row.name || ''} (copy)`.slice(0, 200));
    setEditDescription(row.description || '');
    setEditGroupIds(Array.isArray(row.group_ids) ? [...row.group_ids] : []);
    setEditOpen(true);
    toast.info('Review the short name and save as a new custom template.');
  };

  const closeEdit = () => {
    setEditOpen(false);
    setEditSaving(false);
  };

  const saveEdit = async () => {
    const nm = normalizeTemplateShortName(editName);
    if (!nm) {
      toast.error('Enter a template short name (shown as User Creation - …).');
      return;
    }
    setEditSaving(true);
    try {
      if (editMode === 'create') {
        await api.post('/users/group-assignment-templates', {
          name: nm,
          description: editDescription.trim() || null,
          group_ids: editGroupIds,
        });
        toast.success('Template created.');
      } else {
        await api.put(`/users/group-assignment-templates/${editId}`, {
          name: nm,
          description: editDescription.trim() || null,
          group_ids: editGroupIds,
        });
        toast.success('Template updated.');
      }
      closeEdit();
      loadAll();
    } catch (e) {
      toast.error(e.response?.data?.message || 'Save failed');
    } finally {
      setEditSaving(false);
    }
  };

  const deleteCustom = async (row) => {
    if (!window.confirm(`Delete "${formatUserCreationTemplateTitle(row.name)}"?`)) return;
    try {
      await api.delete(`/users/group-assignment-templates/${row.id}`);
      toast.success('Template deleted.');
      loadAll();
    } catch (e) {
      toast.error(e.response?.data?.message || 'Delete failed');
    }
  };

  const groupNamesForIds = (ids) => {
    if (!Array.isArray(ids)) return [];
    return ids.map((id) => groups.find((g) => g.id === id)?.name || id).filter(Boolean);
  };

  return (
    <Box sx={{ p: 2, maxWidth: 1200, mx: 'auto' }}>
      <Typography variant="h5" gutterBottom sx={{ fontWeight: 600 }}>
        User creation templates
      </Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
        Templates define default group assignments when creating users. Each template is shown as{' '}
        <strong>User Creation - …</strong> on this screen and on the user form. Built-ins resolve from your seeded
        groups; custom templates are stored for your tenant.
      </Typography>

      <Card
        variant="outlined"
        sx={(theme) => ({
          bgcolor:
            theme.palette.mode === 'dark'
              ? alpha(theme.palette.primary.main, 0.1)
              : alpha(theme.palette.primary.main, 0.04),
        })}
      >
        <CardContent>
          <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 2 }}>
            <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
              All templates
            </Typography>
            {canModify && (
              <Button variant="contained" size="small" startIcon={<AddIcon />} onClick={openCreate}>
                New custom template
              </Button>
            )}
          </Stack>

          {loading ? (
            <Box display="flex" justifyContent="center" py={4}>
              <CircularProgress />
            </Box>
          ) : rows.length === 0 ? (
            <Typography variant="body2" color="text.secondary">
              No templates returned. Ensure RBAC groups are seeded and try again.
            </Typography>
          ) : (
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell>Title</TableCell>
                  <TableCell>Type</TableCell>
                  <TableCell align="right">Groups</TableCell>
                  <TableCell>Notes</TableCell>
                  <TableCell align="right">Actions</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {rows.map((row) => {
                  const partial = row.is_builtin && row.missing_stems?.length > 0;
                  return (
                    <TableRow key={row.id} hover>
                      <TableCell sx={{ fontWeight: 500 }}>
                        {formatUserCreationTemplateTitle(row.name)}
                      </TableCell>
                      <TableCell>
                        <Chip
                          size="small"
                          label={row.rowKind === 'builtin' ? 'Built-in' : 'Custom'}
                          color={row.rowKind === 'builtin' ? 'default' : 'primary'}
                          variant={row.rowKind === 'builtin' ? 'outlined' : 'filled'}
                        />
                      </TableCell>
                      <TableCell align="right">{(row.group_ids || []).length}</TableCell>
                      <TableCell>
                        <Typography variant="caption" color="text.secondary" component="span">
                          {partial
                            ? `Missing groups: ${row.missing_stems.join(', ')}`
                            : (row.description || '').slice(0, 80) || '—'}
                          {(row.description || '').length > 80 ? '…' : ''}
                        </Typography>
                      </TableCell>
                      <TableCell align="right">
                        <Button size="small" startIcon={<ViewIcon />} onClick={() => openView(row)}>
                          View
                        </Button>
                        {row.rowKind === 'custom' && canModify && (
                          <>
                            <Button size="small" startIcon={<EditIcon />} onClick={() => openEditCustom(row)}>
                              Edit
                            </Button>
                            <Button size="small" color="error" startIcon={<DeleteIcon />} onClick={() => deleteCustom(row)}>
                              Delete
                            </Button>
                          </>
                        )}
                        {row.rowKind === 'builtin' && canModify && (
                          <Button size="small" startIcon={<CopyIcon />} onClick={() => copyBuiltinAsCustom(row)}>
                            Copy as custom
                          </Button>
                        )}
                      </TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          )}
        </CardContent>
      </Card>

      <Dialog open={viewOpen} onClose={closeView} fullWidth maxWidth="sm">
        <DialogTitle>{viewRow ? formatUserCreationTemplateTitle(viewRow.name) : ''}</DialogTitle>
        <DialogContent dividers>
          {viewRow?.description ? (
            <Typography variant="body2" sx={{ mb: 2 }}>
              {viewRow.description}
            </Typography>
          ) : null}
          {viewRow?.is_builtin && viewRow?.missing_stems?.length > 0 && (
            <Alert severity="warning" sx={{ mb: 2 }}>
              Some seed groups are missing in this environment: {viewRow.missing_stems.join(', ')}.
            </Alert>
          )}
          {viewRow?.group_stems?.length > 0 && (
            <Typography variant="caption" color="text.secondary" display="block" sx={{ mb: 1 }}>
              Defined from group names: {viewRow.group_stems.join(', ')}
            </Typography>
          )}
          <Typography variant="subtitle2" sx={{ mb: 1 }}>
            Assigned groups ({(viewRow?.group_ids || []).length})
          </Typography>
          <Box component="ul" sx={{ m: 0, pl: 2 }}>
            {groupNamesForIds(viewRow?.group_ids || []).map((n) => (
              <Typography key={n} component="li" variant="body2">
                {n}
              </Typography>
            ))}
          </Box>
          {(viewRow?.group_ids || []).length === 0 && (
            <Typography variant="body2" color="text.secondary">
              No groups resolved yet.
            </Typography>
          )}
        </DialogContent>
        <DialogActions>
          {viewRow?.rowKind === 'builtin' && canModify && (
            <Button startIcon={<CopyIcon />} onClick={() => { closeView(); copyBuiltinAsCustom(viewRow); }}>
              Copy as custom
            </Button>
          )}
          <Button onClick={closeView}>Close</Button>
        </DialogActions>
      </Dialog>

      <Dialog open={editOpen} onClose={() => !editSaving && closeEdit()} fullWidth maxWidth="md">
        <DialogTitle>{editMode === 'create' ? 'New custom template' : 'Edit custom template'}</DialogTitle>
        <DialogContent dividers>
          <Stack spacing={2}>
            <TextField
              label="Short name"
              value={editName}
              onChange={(e) => setEditName(e.target.value)}
              fullWidth
              required
              disabled={editSaving}
              helperText={`Displayed everywhere as: ${formatUserCreationTemplateTitle(normalizeTemplateShortName(editName) || '…')}`}
            />
            <TextField
              label="Description"
              value={editDescription}
              onChange={(e) => setEditDescription(e.target.value)}
              fullWidth
              multiline
              minRows={2}
              disabled={editSaving}
            />
            <Box
              sx={(theme) => ({
                p: 1.5,
                borderRadius: 2,
                border: 1,
                borderColor: 'divider',
                bgcolor:
                  theme.palette.mode === 'dark'
                    ? alpha(theme.palette.divider, 0.18)
                    : alpha(theme.palette.grey[500], 0.06),
              })}
            >
              <Typography variant="subtitle2" sx={{ mb: 1 }}>
                Groups in this template
              </Typography>
              <UserGroupAssignmentPicker
                hideTemplateToolbar
                groups={groups}
                value={editGroupIds}
                onChange={setEditGroupIds}
                disabled={editSaving}
                canManageTemplates={false}
              />
            </Box>
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={closeEdit} disabled={editSaving}>
            Cancel
          </Button>
          <Button variant="contained" onClick={saveEdit} disabled={editSaving}>
            {editSaving ? 'Saving…' : editMode === 'create' ? 'Create' : 'Save'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}
